import { LitElement, html, css, nothing } from 'lit';
import { generationChanged } from '../utils/constants.js';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import { solveConstraints } from '@arctek/sysml-core';
import type { SolverResult, ConstraintResult } from '@arctek/sysml-core';

// ---------------------------------------------------------------------------
// <sysml-solver-panel> -- Parametric constraint solver results
//
// Displays constraint evaluation results from the solver engine.
// Shows: satisfied/violated/error counts, individual constraint details,
// dependency graph info, and circular dependency warnings.
// ---------------------------------------------------------------------------

@customElement('sysml-solver-panel')
export class SysmlSolverPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
      }
      .solver-header {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .solver-title {
        font-size: var(--r5-text-sm);
        font-weight: 600;
        flex: 1;
      }
      .summary-badges {
        display: flex;
        gap: var(--r5-space-1);
      }
      .constraint-list {
        flex: 1;
        overflow-y: auto;
        padding: var(--r5-space-2);
      }
      .constraint-item {
        display: flex;
        align-items: flex-start;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2);
        border-radius: var(--r5-radius-md);
        margin-bottom: var(--r5-space-1);
        cursor: pointer;
        transition: background var(--r5-duration-fast) var(--r5-ease-default);
      }
      .constraint-item:hover {
        background: color-mix(in srgb, var(--r5-text) 5%, transparent);
      }
      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
        margin-top: 5px;
      }
      .status-dot.satisfied { background: var(--r5-success-500, #22c55e); }
      .status-dot.violated { background: var(--r5-error-500, #ef4444); }
      .status-dot.warning { background: var(--r5-warning-500, #f59e0b); }
      .constraint-info {
        flex: 1;
        min-width: 0;
      }
      .constraint-name {
        font-size: var(--r5-text-sm);
        font-weight: 500;
      }
      .constraint-expr {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        font-family: var(--r5-font-mono);
        margin-top: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .constraint-message {
        font-size: var(--r5-font-size-badge);
        margin-top: 2px;
      }
      .constraint-message.error { color: var(--r5-error-500, #ef4444); }
      .constraint-message.warning { color: var(--r5-warning-500, #f59e0b); }
      .constraint-message.info { color: var(--r5-info-500, #3b82f6); }
      .binding-list {
        display: flex;
        gap: var(--r5-space-2);
        flex-wrap: wrap;
        margin-top: var(--r5-space-1);
      }
      .binding {
        font-size: var(--r5-font-size-badge);
        font-family: var(--r5-font-mono);
        background: color-mix(in srgb, var(--r5-primary) 8%, transparent);
        padding: 1px 6px;
        border-radius: var(--r5-radius-sm);
        color: var(--r5-primary);
      }
      .cycle-warning {
        padding: var(--r5-space-2) var(--r5-space-3);
        background: color-mix(in srgb, var(--r5-warning-500) 10%, transparent);
        border-radius: var(--r5-radius-md);
        margin-bottom: var(--r5-space-2);
        font-size: var(--r5-text-xs);
        color: var(--r5-warning-500);
      }
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-6);
        color: var(--r5-text-muted);
        text-align: center;
      }
      .empty-state p {
        margin: 0;
        font-size: var(--r5-text-sm);
      }
      .run-btn {
        margin-left: auto;
      }
    `,
  ];

  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;

  @state() private _result: SolverResult | null = null;

  override updated(changedProps: Map<string, unknown>): void {
    if (changedProps.has('modeler') && this.modeler?.parsedModel) {
      this._solve();
    }
  }

  private _solve(): void {
    if (!this.modeler.parsedModel) {
      this._result = null;
      return;
    }
    try {
      this._result = solveConstraints(this.modeler.parsedModel);
    } catch {
      this._result = null;
    }
  }

  override render() {
    if (!this._result || this._result.totalConstraints === 0) {
      return this._renderEmpty();
    }

    return html`
      ${this._renderHeader()}
      <div class="constraint-list">
        ${this._result.cycles.length > 0 ? this._renderCycleWarnings() : nothing}
        ${this._result.constraintResults.map(r => this._renderConstraint(r))}
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="empty-state">
        <p>No constraints defined</p>
        <p style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted);">
          Add <code>constraint def</code> blocks to your model to see solver results
        </p>
      </div>
    `;
  }

  private _renderHeader() {
    const r = this._result!;
    return html`
      <div class="solver-header">
        <span class="solver-title">Constraint Solver</span>
        <div class="summary-badges">
          ${r.satisfied > 0 ? html`<span class="badge badge-success">${r.satisfied} satisfied</span>` : nothing}
          ${r.violated > 0 ? html`<span class="badge badge-error">${r.violated} violated</span>` : nothing}
          ${r.errors > 0 ? html`<span class="badge badge-warning">${r.errors} error${r.errors !== 1 ? 's' : ''}</span>` : nothing}
        </div>
      </div>
    `;
  }

  private _renderCycleWarnings() {
    // Build ID → short name lookup from constraint results
    const nameMap = new Map<string, string>();
    for (const cr of this._result!.constraintResults) {
      nameMap.set(cr.constraintId, cr.constraintName);
    }

    return this._result!.cycles.map(
      cycle => {
        const names = cycle.map(id => nameMap.get(id) ?? id.split('.').pop() ?? id);
        return html`
          <div class="cycle-warning">
            Circular dependency detected: ${names.join(' -> ')}
          </div>
        `;
      },
    );
  }

  private _renderConstraint(cr: ConstraintResult) {
    const statusClass = cr.satisfied ? 'satisfied' : cr.diagnostics.some(d => d.severity === 'error') ? 'violated' : 'warning';
    // Get expression from the model element
    const el = this.modeler.allElements.find(e => e.id === cr.constraintId);
    const expression = el && 'expression' in el ? (el as { expression?: string }).expression : undefined;

    return html`
      <div class="constraint-item" @click=${() => this._onConstraintClick(cr)}>
        <span class="status-dot ${statusClass}"></span>
        <div class="constraint-info">
          <div class="constraint-name">${cr.constraintName}</div>
          ${expression ? html`<div class="constraint-expr">${expression}</div>` : nothing}
          ${cr.bindings.size > 0
            ? html`
                <div class="binding-list">
                  ${[...cr.bindings.entries()].map(
                    ([k, v]) => html`<span class="binding">${k} = ${v}</span>`,
                  )}
                </div>
              `
            : nothing}
          ${cr.diagnostics.map(
            d => html`<div class="constraint-message ${d.severity}">${d.message}</div>`,
          )}
        </div>
      </div>
    `;
  }

  private _onConstraintClick(cr: ConstraintResult): void {
    // Find the constraint element and reveal its source
    const el = this.modeler.allElements.find(e => e.id === cr.constraintId);
    if (el?.source) {
      this.modeler.revealInEditor(el.source);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-solver-panel': SysmlSolverPanel;
  }
}
